/**
 * Identity Variants API client helpers (LF-5566 PROV-21).
 *
 * Plain async functions backed by directApiClient — the entity screens' read /
 * add / edit / delete path for the name, address and holding-reference rows that
 * feed share-registry letters and court-form alias recitals. Throws on error.
 *
 * Creates are find-or-create server-side: re-adding a value the AI already
 * extracted returns the existing row rather than duplicating it.
 */

import { directApiClient } from '@/lib/api/direct-client';
import type {
  AddressVariant,
  AssetIdentityReference,
  CreateAddressVariantRequest,
  CreateAssetIdentityReferenceRequest,
  CreateNameVariantRequest,
  IdentityVariantOwnerType,
  IdentityVariantsResponse,
  NameVariant,
  UpdateAddressVariantRequest,
  UpdateAssetIdentityReferenceRequest,
  UpdateNameVariantRequest
} from './types';

const BASE_PATH = '/identity-variants';

const ownerPath = (ownerType: IdentityVariantOwnerType, ownerId: number) =>
  `${BASE_PATH}/${ownerType}/${ownerId}`;

export async function getIdentityVariants(
  ownerType: IdentityVariantOwnerType,
  ownerId: number
): Promise<IdentityVariantsResponse> {
  return directApiClient.get<IdentityVariantsResponse>(
    ownerPath(ownerType, ownerId)
  );
}

// ── Name variants ────────────────────────────────────────────────────────────

export async function createNameVariant(
  ownerType: IdentityVariantOwnerType,
  ownerId: number,
  data: CreateNameVariantRequest
): Promise<NameVariant> {
  return directApiClient.post<NameVariant>(
    `${ownerPath(ownerType, ownerId)}/names`,
    data
  );
}

export async function updateNameVariant(
  id: number,
  data: UpdateNameVariantRequest
): Promise<NameVariant> {
  return directApiClient.patch<NameVariant>(`${BASE_PATH}/names/${id}`, data);
}

export async function deleteNameVariant(id: number): Promise<void> {
  return directApiClient.delete<void>(`${BASE_PATH}/names/${id}`);
}

// ── Address variants ─────────────────────────────────────────────────────────

export async function createAddressVariant(
  ownerType: IdentityVariantOwnerType,
  ownerId: number,
  data: CreateAddressVariantRequest
): Promise<AddressVariant> {
  return directApiClient.post<AddressVariant>(
    `${ownerPath(ownerType, ownerId)}/addresses`,
    data
  );
}

export async function updateAddressVariant(
  id: number,
  data: UpdateAddressVariantRequest
): Promise<AddressVariant> {
  return directApiClient.patch<AddressVariant>(
    `${BASE_PATH}/addresses/${id}`,
    data
  );
}

export async function deleteAddressVariant(id: number): Promise<void> {
  return directApiClient.delete<void>(`${BASE_PATH}/addresses/${id}`);
}

// ── Asset-identity references ────────────────────────────────────────────────

export async function createAssetIdentityReference(
  ownerType: IdentityVariantOwnerType,
  ownerId: number,
  data: CreateAssetIdentityReferenceRequest
): Promise<AssetIdentityReference> {
  return directApiClient.post<AssetIdentityReference>(
    `${ownerPath(ownerType, ownerId)}/asset-references`,
    data
  );
}

export async function updateAssetIdentityReference(
  id: number,
  data: UpdateAssetIdentityReferenceRequest
): Promise<AssetIdentityReference> {
  return directApiClient.patch<AssetIdentityReference>(
    `${BASE_PATH}/asset-references/${id}`,
    data
  );
}

export async function deleteAssetIdentityReference(id: number): Promise<void> {
  return directApiClient.delete<void>(`${BASE_PATH}/asset-references/${id}`);
}
